import { useEffect, useRef, useState } from "react";
import { Plus, Edit, Trash2, GripVertical, X, Image as ImageIcon } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";

export default function ContentManager({ collection, title, fields, blank }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [picker, setPicker] = useState(null); // {mode:'field'|'rte', key}
  const dragIdx = useRef(null);
  const insertRef = useRef(null);

  const load = () => api.get(`/admin/content-items/${collection}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [collection]);

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "tags" && typeof payload[f.key] === "string") payload[f.key] = payload[f.key].split(",").map((t) => t.trim()).filter(Boolean); });
    try {
      if (form.id) await api.put(`/admin/content-items/${collection}/${form.id}`, payload);
      else await api.post(`/admin/content-items/${collection}`, payload);
      setForm(null); load();
    } catch (e) { alert("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  const del = async (id) => { if (window.confirm("Delete this item?")) { await api.delete(`/admin/content-items/${collection}/${id}`); load(); } };

  const onDrop = async (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setItems(next);
    dragIdx.current = null;
    await api.put(`/admin/content-items/${collection}/reorder`, { order: next.map((x) => x.id) });
  };

  const label = (it) => it.title || it.name || it.quote?.slice(0, 40) || "Untitled";
  const inputCls = "w-full border border-lp-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-lp-cyan";

  const editForm = (edit) => setForm(edit ? { ...edit, ...Object.fromEntries(fields.filter((f) => f.type === "tags").map((f) => [f.key, (edit[f.key] || []).join(", ")])) } : { ...blank });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-heading font-700 text-2xl text-lp-blue">{title}</h1>
          <span className="text-sm text-lp-muted bg-white border border-lp-border rounded-sm px-2.5 py-0.5">{items.length}</span>
        </div>
        <button onClick={() => editForm(null)} data-testid={`add-${collection}`} className="inline-flex items-center gap-2 bg-lp-blue text-white font-600 px-4 py-2.5 rounded-sm text-sm"><Plus size={15} /> New</button>
      </div>
      <p className="text-xs text-lp-muted mb-4">Drag rows to reorder how they appear on the site.</p>

      <div className="bg-white border border-lp-border rounded-sm divide-y divide-lp-border">
        {items.map((it, i) => (
          <div key={it.id} draggable onDragStart={() => (dragIdx.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
            className="flex items-center gap-4 p-4 hover:bg-lp-grey/50" data-testid={`${collection}-row-${i}`}>
            <GripVertical size={18} className="text-lp-border cursor-grab shrink-0" />
            {(it.image || it.logo) && <img src={mediaUrl(it.image || it.logo)} alt="" className="h-10 w-10 object-cover rounded-sm border border-lp-border shrink-0" onError={(e) => (e.target.style.display = "none")} />}
            <div className="flex-1 min-w-0">
              <div className="font-600 text-lp-ink truncate">{label(it)}</div>
              <div className="text-xs text-lp-muted">{it.category || it.location || it.role || it.icon || ""}</div>
            </div>
            {it.status === "draft" && <span className="text-[11px] font-600 bg-lp-grey text-lp-muted px-2 py-0.5 rounded-sm">draft</span>}
            <button onClick={() => editForm(it)} className="text-lp-blue"><Edit size={16} /></button>
            <button onClick={() => del(it.id)} className="text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="p-10 text-center text-lp-muted">No items yet.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-lp-ink/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setForm(null)}>
          <div className="bg-white rounded-sm max-w-2xl w-full p-8 space-y-4 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-700 text-xl text-lp-blue">{form.id ? "Edit" : "New"} {title.replace(/s$/, "")}</h3>
              <button onClick={() => setForm(null)}><X size={20} className="text-lp-muted" /></button>
            </div>
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-xs font-600 text-lp-ink">{f.label}{f.hint ? <span className="text-lp-muted font-400"> — {f.hint}</span> : ""}</label>
                {f.type === "text" && <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls + " mt-1"} data-testid={`field-${f.key}`} />}
                {f.type === "tags" && <input data-testid={`field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder="comma separated" className={inputCls + " mt-1"} />}
                {f.type === "textarea" && <textarea data-testid={`field-${f.key}`} rows={3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls + " mt-1"} />}
                {f.type === "status" && (
                  <select data-testid={`field-${f.key}`} value={form[f.key] || "published"} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls + " mt-1"}>
                    <option value="published">Published</option><option value="draft">Draft</option>
                  </select>
                )}
                {f.type === "image" && (
                  <div className="flex items-center gap-3 mt-1">
                    {form[f.key] ? <img src={mediaUrl(form[f.key])} alt="" className="h-14 w-14 object-cover rounded-sm border border-lp-border" onError={(e) => (e.target.style.display = "none")} /> : <div className="h-14 w-14 bg-lp-grey rounded-sm flex items-center justify-center"><ImageIcon size={20} className="text-lp-muted" /></div>}
                    <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder="Image URL or pick from library" className={inputCls} />
                    <button type="button" onClick={() => setPicker({ mode: "field", key: f.key })} className="shrink-0 border border-lp-blue text-lp-blue font-600 px-3 py-2 rounded-sm text-sm">Library</button>
                  </div>
                )}
                {f.type === "richtext" && (
                  <div className="mt-1">
                    <RichTextEditor value={form[f.key] || ""} onChange={(html) => setForm((prev) => ({ ...prev, [f.key]: html }))} insertRef={insertRef} onOpenLibrary={() => setPicker({ mode: "rte" })} />
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={save} data-testid={`save-${collection}`} className="bg-lp-blue text-white font-600 px-6 py-2.5 rounded-sm">Save</button>
              <button onClick={() => setForm(null)} className="border border-lp-border px-6 py-2.5 rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <MediaPicker open={!!picker} onClose={() => setPicker(null)} onSelect={(url) => {
        if (picker?.mode === "field") setForm((prev) => ({ ...prev, [picker.key]: url }));
        else if (picker?.mode === "rte" && insertRef.current) insertRef.current(url);
      }} />
    </div>
  );
}
